import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { ContactCard } from "../components/ContactCard";
import { OrderContactEditorDialog } from "../components/OrderContactEditorDialog";
import { DocumentsOrderContact, DocumentsOrderInfo } from "../types";

export function ContactsDrawer({
  open,
  orderNumber,
  orderInfo,
  emailSubjectLine,
  contacts,
  loading,
  error,
  onContactsChanged,
  onClose
}: {
  open: boolean;
  orderNumber: string;
  orderInfo?: DocumentsOrderInfo;
  emailSubjectLine?: string;
  contacts: DocumentsOrderContact[];
  loading: boolean;
  error: string | null;
  onContactsChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const [showEmptyContacts, setShowEmptyContacts] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<DocumentsOrderContact | null>(null);
  const drawerPaperRef = useRef<HTMLDivElement | null>(null);
  const visibleContacts = useMemo(
    () => contacts.filter((contact) => contact.type && (showEmptyContacts || contact.name)),
    [contacts, showEmptyContacts]
  );

  const openAddContact = () => {
    setEditingContact(null);
    setIsEditorOpen(true);
  };

  const openEditContact = (contact: DocumentsOrderContact) => {
    setEditingContact(contact);
    setIsEditorOpen(true);
  };

  useEffect(() => {
    if (!open || isEditorOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      if (drawerPaperRef.current?.contains(target)) {
        return;
      }

      if (target.closest(".MuiPopover-root, .MuiPopper-root, .MuiDialog-root")) {
        return;
      }

      onClose();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isEditorOpen, onClose, open]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{
        ref: drawerPaperRef,
        sx: {
          width: { xs: "100%", sm: 460 },
          maxWidth: "100%"
        }
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.3 }}>
              Contacts
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="contained"
                startIcon={<Iconify icon="mdi:plus" width={18} />}
                onClick={openAddContact}
              >
                Add
              </Button>
              <IconButton size="small" onClick={onClose}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              {orderNumber}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Switch
                size="small"
                checked={showEmptyContacts}
                onChange={() => setShowEmptyContacts((current) => !current)}
              />
              <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "nowrap", fontSize: 12 }}>
                Show Empty Contacts
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", overscrollBehavior: "contain" }}>
          {loading ? (
            <Stack spacing={1.25} sx={{ p: 2 }}>
              {[0, 1, 2, 3].map((index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Skeleton variant="text" width="45%" height={28} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="65%" />
                </Paper>
              ))}
            </Stack>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : visibleContacts.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">No contacts found for this order.</Alert>
            </Box>
          ) : (
            <Box>
              {visibleContacts.map((contact) => (
                <ContactCard
                  key={`${contact.id || contact.lookupCode || contact.code}-${contact.type}`}
                  contact={contact}
                  orderNumber={orderNumber}
                  emailSubjectLine={emailSubjectLine}
                  onContactsChanged={onContactsChanged}
                  onEdit={openEditContact}
                />
              ))}
            </Box>
          )}
        </Box>
      </Stack>
      <OrderContactEditorDialog
        open={isEditorOpen}
        mode={editingContact ? "edit" : "add"}
        orderNumber={orderNumber}
        orderInfo={orderInfo}
        contacts={contacts}
        contact={editingContact}
        onClose={() => setIsEditorOpen(false)}
        onSaved={onContactsChanged}
      />
    </Drawer>
  );
}
